/* eslint-disable react/prop-types */
import { Box, CircularProgress, Tooltip, Typography } from '@material-ui/core'
import MicIcon from '@material-ui/icons/Mic'
import StopIcon from '@material-ui/icons/Stop'
import React from 'react'

import { transcribeInteractionAudio } from 'api/communication'

import { blobToWav } from './wav'

// How the mic decides you're done: after this many ms of continuous quiet
// (once you've actually started speaking), it auto-stops. Kept generous (6s)
// so a pause to think doesn't cut you off. A hard cap stops any runaway.
const SILENCE_MS = 6000
const MAX_MS = 60000
const POLL_MS = 200
// RMS levels on the mic input: above SPEAK = you're talking; below SILENCE =
// quiet. The gap avoids flip-flopping on breaths/background hum.
const SPEAK_RMS = 0.03
const SILENCE_RMS = 0.015

const fmt = (ms) => {
  const s = Math.floor(ms / 1000)
  return `0:${String(s).padStart(2, '0')}`
}

// A mic button that records a short spoken note, converts it to WAV, sends it
// to the backend for transcription + (for templated topics) field extraction,
// and hands the result back via onResult. It only ever pre-fills — the caller
// drops the result into the form for the team to review and save.
const VoiceCapture = ({ topic = '', fields = [], onResult, tooltip }) => {
  const [state, setState] = React.useState('idle') // idle|recording|processing|error
  const [elapsed, setElapsed] = React.useState(0)
  const [errorMsg, setErrorMsg] = React.useState('')

  // Always call the freshest onResult — the caller passes a new closure each
  // render (it merges into current form state), and recording may finish after
  // several re-renders.
  const onResultRef = React.useRef(onResult)
  onResultRef.current = onResult

  const mediaRecorderRef = React.useRef(null)
  const streamRef = React.useRef(null)
  const audioCtxRef = React.useRef(null)
  const chunksRef = React.useRef([])
  const pollRef = React.useRef(null)
  const startTsRef = React.useRef(0)
  const silenceStartRef = React.useRef(0)
  const hasSpokenRef = React.useRef(false)
  const stoppingRef = React.useRef(false)

  const cleanupCapture = React.useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    if (audioCtxRef.current) {
      if (audioCtxRef.current.close) audioCtxRef.current.close()
      audioCtxRef.current = null
    }
  }, [])

  const stopRecording = React.useCallback(() => {
    if (stoppingRef.current) return
    stoppingRef.current = true
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    const rec = mediaRecorderRef.current
    if (rec && rec.state !== 'inactive') {
      rec.stop() // fires onstop → assemble + send
    }
  }, [])

  const process = React.useCallback(
    async (blob) => {
      setState('processing')
      try {
        const wav = await blobToWav(blob)
        const result = await transcribeInteractionAudio({ audio: wav, topic, fields })
        if (result && result.ok) {
          if (onResultRef.current) onResultRef.current(result)
          setState('idle')
        } else {
          setErrorMsg(
            result && result.detail
              ? `Voice failed — ${result.detail}`
              : "Couldn't catch that — please try again or type it."
          )
          setState('error')
        }
      } catch (e) {
        setErrorMsg("Couldn't process the audio — please type it.")
        setState('error')
      }
    },
    [topic, fields]
  )

  const startRecording = React.useCallback(async () => {
    setErrorMsg('')
    setElapsed(0)
    chunksRef.current = []
    hasSpokenRef.current = false
    silenceStartRef.current = 0
    stoppingRef.current = false

    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (e) {
      setErrorMsg('Microphone permission is needed to record.')
      setState('error')
      return
    }
    streamRef.current = stream

    let recorder
    try {
      recorder = new MediaRecorder(stream)
    } catch (e) {
      cleanupCapture()
      setErrorMsg('Recording is not supported on this browser.')
      setState('error')
      return
    }
    mediaRecorderRef.current = recorder
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size) chunksRef.current.push(e.data)
    }
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, {
        type: chunksRef.current[0]?.type || 'audio/webm'
      })
      cleanupCapture()
      if (blob.size) {
        process(blob)
      } else {
        setState('idle')
      }
    }
    recorder.start()
    startTsRef.current = Date.now()
    setState('recording')

    // Level metering for silence auto-stop + a hard time cap.
    const AudioCtx = window.AudioContext || window.webkitAudioContext
    let analyser = null
    let data = null
    try {
      const ctx = new AudioCtx()
      audioCtxRef.current = ctx
      const src = ctx.createMediaStreamSource(stream)
      analyser = ctx.createAnalyser()
      analyser.fftSize = 2048
      src.connect(analyser)
      data = new Float32Array(analyser.fftSize)
    } catch (e) {
      analyser = null // metering unavailable → rely on the hard cap only
    }

    pollRef.current = setInterval(() => {
      const now = Date.now()
      setElapsed(now - startTsRef.current)

      if (now - startTsRef.current >= MAX_MS) {
        stopRecording()
        return
      }
      if (!analyser) return

      analyser.getFloatTimeDomainData(data)
      let sum = 0
      for (let i = 0; i < data.length; i += 1) sum += data[i] * data[i]
      const rms = Math.sqrt(sum / data.length)

      if (rms > SPEAK_RMS) {
        hasSpokenRef.current = true
        silenceStartRef.current = 0
      } else if (hasSpokenRef.current && rms < SILENCE_RMS) {
        if (!silenceStartRef.current) silenceStartRef.current = now
        else if (now - silenceStartRef.current >= SILENCE_MS) stopRecording()
      }
    }, POLL_MS)
  }, [cleanupCapture, process, stopRecording])

  // Stop everything if the form/drawer unmounts mid-recording.
  React.useEffect(() => () => {
    stopRecording()
    cleanupCapture()
  }, [cleanupCapture, stopRecording])

  const onButtonClick = () => {
    if (state === 'recording') stopRecording()
    else if (state !== 'processing') startRecording()
  }

  const recording = state === 'recording'
  const processing = state === 'processing'

  const pill = (children, bg, fg, border) => (
    <Box
      onClick={processing ? undefined : onButtonClick}
      display="inline-flex"
      alignItems="center"
      style={{
        gap: 6,
        cursor: processing ? 'default' : 'pointer',
        background: bg,
        color: fg,
        border: `1px solid ${border}`,
        borderRadius: 20,
        padding: '4px 12px',
        fontSize: 12,
        fontWeight: 600,
        userSelect: 'none'
      }}
    >
      {children}
    </Box>
  )

  let control
  if (recording) {
    control = pill(
      <>
        <StopIcon style={{ fontSize: 16 }} />
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#D64545',
            animation: 'ohVoicePulse 1s ease-in-out infinite'
          }}
        />
        <span>{fmt(elapsed)} · Tap to stop</span>
      </>,
      '#FBEDE3',
      '#B23A2E',
      '#F1D9C7'
    )
  } else if (processing) {
    control = pill(
      <>
        <CircularProgress size={13} thickness={5} style={{ color: '#B26A3C' }} />
        <span>Transcribing…</span>
      </>,
      '#F7F5F2',
      '#6B7A85',
      '#E6E1DB'
    )
  } else {
    control = (
      <Tooltip title={tooltip || 'Fill by voice'}>
        {pill(
          <>
            <MicIcon style={{ fontSize: 16 }} />
            <span>Speak</span>
          </>,
          '#fff',
          '#B26A3C',
          '#E8B98F'
        )}
      </Tooltip>
    )
  }

  return (
    <Box>
      <style>{'@keyframes ohVoicePulse{0%,100%{opacity:1}50%{opacity:.25}}'}</style>
      {control}
      {state === 'error' && errorMsg ? (
        <Typography variant="caption" color="error" display="block" style={{ marginTop: 4 }}>
          {errorMsg}
        </Typography>
      ) : null}
    </Box>
  )
}

export default VoiceCapture
